import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";

const DEFAULT_TIER = "Growth";

/**
 * Shown when the user does not have access to a tier-gated tool.
 * - requiredTier: plan name; defaults to the paid plan so the copy never reads "part of ".
 * - signedIn: true -> "View plans" (/subscribe); false -> "Sign in to unlock" (/login?redirect=…)
 * - redirectPath: path to return to after login (e.g. /grant-application-toolkit)
 *
 * Note: `useTierGate` currently returns `isLocked: false` for everyone, so this view only mounts
 * once that hook is wired to the real plan.
 */
export default function TierLockedView({ requiredTier, signedIn, redirectPath = "/dashboard" }) {
  const navigate = useNavigate();
  const tierLabel = String(requiredTier || "").trim() || DEFAULT_TIER;

  const handleViewPlans = useCallback(() => {
    navigate("/subscribe");
  }, [navigate]);

  const handleSignIn = useCallback(() => {
    navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`);
  }, [navigate, redirectPath]);

  const handleCta = signedIn ? handleViewPlans : handleSignIn;
  const ctaLabel = signedIn ? "View plans" : "Sign in to unlock";

  return (
    <div
      className="min-h-[60vh] flex items-center justify-center p-6"
      data-testid="tier-locked-view"
    >
      <div
        role="region"
        aria-labelledby="tier-locked-title"
        className="w-full max-w-lg bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden"
        style={{ borderColor: "rgba(96, 165, 250, 0.3)" }}
      >
        <div className="absolute top-6 right-6">
          <Lock className="w-8 h-8" style={{ color: "#60A5FA" }} aria-hidden />
        </div>

        <div className="relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6" style={{ backgroundColor: "rgba(96, 165, 250, 0.1)", borderColor: "rgba(96, 165, 250, 0.3)" }}>
            <span className="text-sm font-semibold" style={{ color: "#60A5FA" }}>{tierLabel}</span>
          </div>
          <h1 id="tier-locked-title" className="text-2xl md:text-3xl font-bold mb-4">
            This feature is part of the {tierLabel} plan
          </h1>
          <p className="text-base mb-3 max-w-md mx-auto" style={{ color: "#4B5563" }}>
            {signedIn
              ? `Upgrade to ${tierLabel} to use this tool.`
              : "Sign in to unlock this feature and continue your grant journey."}
          </p>
          <p className="text-sm mb-8 max-w-md mx-auto" style={{ color: "#6B7280" }}>
            {tierLabel} unlocks the application toolkit, the application generator and the full eligibility report, alongside the assessment you already have.
          </p>

          <button
            type="button"
            onClick={handleCta}
            className="px-6 py-3 rounded-full font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50 focus-visible:ring-offset-2 bg-white border inline-flex items-center gap-2 mx-auto"
            style={{ color: "#60A5FA", borderColor: "rgba(96, 165, 250, 0.5)" }}
          >
            {ctaLabel}
            <ArrowRight size={20} className="inline" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}
